import React, { ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Alert } from '@mui/material';
import {
  CurrencyAttributes,
  SetCurrencyBody,
  createDefaultCurrencyAttributes,
} from '@loyalty/shared-common/gamification-api-client';
import { defaultCurrencyImage, uuid } from '@loyalty/shared-common';
import { useAlerts } from '@qualifio/ui-commons';
import { useTranslation } from 'react-i18next';
import { IsLoading, useLoaders } from '../shared/context/context-utils';
import { Loader } from '../shared/layout/Loader';
import { useApi } from '../shared/api-client/ApiClientProvider';
import { useProgram } from '../loyalty/ProgramContext';
import { useSelectiveEffect } from '../shared/use-selective-effect';

export interface CurrenciesContextInterface {
  currencies: CurrencyAttributes[];
  currenciesById: Record<string, CurrencyAttributes>;
  selectedCurrency: CurrencyAttributes | undefined;
  createCurrency: () => void;
  setCurrency: (currency: SetCurrencyBody) => void;
  archiveCurrency: (currencyId: string) => Promise<void>;
  deleteUnsavedCurrency: (currencyId: string) => void;
  deleteUnsavedCurrencies: () => void;
  restoreCurrency: (currencyId: string) => Promise<void>;
  setSelectedCurrency: (currency: CurrencyAttributes | undefined) => void;
  isLoading: IsLoading;
}

export const CurrenciesContext = createContext<CurrenciesContextInterface>({} as any);

export function useCurrencies() {
  return useContext(CurrenciesContext);
}

export function CurrenciesProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { notify } = useAlerts();
  const { program } = useProgram();
  const { gamificationApi, managerApi } = useApi();
  const [error, setError] = useState<any>();
  const [currencies, setCurrencies] = useState<CurrencyAttributes[]>(undefined as any);
  const [selectedCurrency, setSelectedCurrency] = useState<CurrencyAttributes | undefined>(
    undefined,
  );

  const { addLoader, removeLoader, isLoading } = useLoaders({ getCurrencies: true });

  const createCurrency = useCallback(() => {
    const i18nAttributes = program.languages.reduce(
      (langs, current) => ({
        ...langs,
        [current]: {
          name: '',
          description: '',
          image: defaultCurrencyImage,
        },
      }),
      {},
    );

    const currency = {
      ...createDefaultCurrencyAttributes({
        i18nAttributes,
      }),
      id: uuid(),
      isNew: true,
    };

    setCurrencies(prev => [currency, ...prev]);
    setSelectedCurrency(currency);
  }, [program.languages]);

  const setCurrency = useCallback(
    (setCurrencyBody: SetCurrencyBody) => {
      addLoader('setCurrency');
      managerApi
        .setCurrency(program.id, setCurrencyBody)
        .then(updatedCurrency => {
          setCurrencies(prev =>
            prev.map(currency => (currency.id === setCurrencyBody.id ? updatedCurrency : currency)),
          );
          setSelectedCurrency(updatedCurrency);
          notify(t('shared.actions.saved'), 'success');
        })
        .catch(error => notify(t('currencies.errors.failed-save-rules', { error }), 'error'))
        .finally(() => removeLoader('setCurrency'));
    },
    [addLoader, managerApi, program.id, notify, removeLoader, t],
  );

  const getCurrencies = useCallback(() => {
    addLoader('getCurrencies');

    gamificationApi
      .getCurrencies(program.id)
      .then(setCurrencies)
      .catch(setError)
      .finally(() => removeLoader('getCurrencies'));
  }, [addLoader, gamificationApi, program.id, removeLoader]);

  const archiveCurrency = useCallback(
    (currencyId: string) => {
      addLoader('archiveCurrency');

      return gamificationApi
        .archiveCurrency(currencyId)
        .then(() => {
          const newCurrencies = currencies.map(currency =>
            currency.id === currencyId ? { ...currency, deletedAt: new Date().toJSON() } : currency,
          );
          setSelectedCurrency(undefined);
          setCurrencies(newCurrencies);
        })
        .catch(error => notify(t('currencies.sidebar.archive.error', { error }), 'error'))
        .finally(() => removeLoader('archiveCurrency'));
    },
    [addLoader, currencies, gamificationApi, notify, removeLoader, t],
  );

  const deleteUnsavedCurrency = useCallback((currencyId: string) => {
    setSelectedCurrency(undefined);
    setCurrencies(currencies => currencies.filter(currency => currency.id !== currencyId));
  }, []);

  const deleteUnsavedCurrencies = useCallback(() => {
    setSelectedCurrency(undefined);
    setCurrencies(currencies => currencies.filter(currency => !(currency as any).isNew));
  }, []);

  const restoreCurrency = useCallback(
    async (currencyId: string) => {
      addLoader('restoreCurrency');

      await gamificationApi
        .restoreCurrency(currencyId)
        .then(() => {
          const newCurrencies = currencies.map(currency =>
            currency.id === currencyId ? { ...currency, deletedAt: undefined } : currency,
          );

          setCurrencies(newCurrencies);
          setSelectedCurrency(currencies.find(({ id }) => id === currencyId));
        })
        .catch(error => notify(t('currencies.sidebar.restore.error', { error }), 'error'))
        .finally(() => removeLoader('restoreCurrency'));
    },
    [addLoader, currencies, gamificationApi, notify, removeLoader, t],
  );

  useSelectiveEffect(
    () => {
      getCurrencies();
    },
    [getCurrencies, program.id],
    [program.id],
  );

  const currenciesById = useMemo(
    () => Object.fromEntries(currencies?.map(c => [c.id, c]) || []),
    [currencies],
  );

  const context = {
    currencies,
    currenciesById,
    selectedCurrency,
    createCurrency,
    setCurrency,
    archiveCurrency,
    deleteUnsavedCurrency,
    deleteUnsavedCurrencies,
    restoreCurrency,
    setSelectedCurrency,
    isLoading,
  };

  return isLoading('getCurrencies') ? (
    <Loader />
  ) : error ? (
    <Alert severity="error">{error.toString()}</Alert>
  ) : (
    <CurrenciesContext.Provider value={context}>{children}</CurrenciesContext.Provider>
  );
}
